import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { useSetAtom } from 'jotai';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import Box from '@mui/material/Box';
import useInfiniteQuery from '../../../Hooks/useInfiniteQuery';
import useAPI from '../../../api';
import TemplateMessageDialog from '../../../Components/Dialog/TemplateMessageDialog';
import MassiveSmsWrapper from '../MassiveDialog/MassiveSmsWrapper';
import CustomMassiveActions from '../MassiveDialog/Inputs/CustomMassiveActions';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import { MASSIVE_PROPERTIES } from '../Utils/constants';
import MassiveDetails from '../MassiveDetails/MassiveDetails';
import mapFilters from '../../../Helpers/Filters/mapFilters';
import ContentError from '../../../Components/Error/ContentError';
import SectionPage from '../../../Components/Paper/SectionPage';
import CampaignAccordion from './OldCampaignAccordion';
import groupTemplatesByName from '../../../Helpers/Arrays/groupTemplatesByName';

const campaignEntry = (
  item,
  handleDeleteMassiveSMS,
  templates,
  handleOpenMassiveDetails,
  handleChangeAccordion,
  expanded
) => (
  <CampaignAccordion
    key={item?.id}
    template={templates?.find(({ id }) => id === item?.templateId)?.title}
    data={item}
    handleDeleteMassiveSMS={handleDeleteMassiveSMS}
    handleOpenMassiveDetails={handleOpenMassiveDetails}
    handleChangeAccordion={handleChangeAccordion}
    expanded={expanded}
  />
);

function EmptyMassiveMessagesComponent() {
  const intl = useIntl();
  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        alignItems: "center",
        justifyContent: "center"
      }}>
      <ContentError
        sizes={{
          title: '3rem',
          message: '1.5rem',
        }}
        text={intl.messages.NO_MASSIVE}
        message={intl.messages.NO_MASSIVE_SUBTITLE}
        icon={
          <SpeakerNotesOffIcon sx={{ color: '#acacac', fontSize: '2rem' }} />
        }
      />
    </Box>
  );
}
const totalCustomersInvolved = (sourceArray) =>
  sourceArray.reduce((acc, curr) => acc + (curr?.involvedCustomers || 0), 0);

const MassiveSMSComponents = function MassiveSMSComponents() {
  const API = useAPI();
  const intl = useIntl();
  const methods = useForm();

  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const [massiveSMS, setMassiveSMS] = useState(false);
  const [SMSTemplates, setSMSTemplates] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [dialogPage, setDialogPage] = useState(0);
  const [customersInvolvedCounts, setCustomersInvolvedCounts] = useState([]);
  const [massiveDetails, setMassiveDetails] = useState(null);
  const [searchValues, setSearchValues] = useState(null);
  const [waveSections, setWaveSections] = useState([]);

  const handleOpenMassiveSMSDialog = () => {
    setMassiveSMS(true);
  };
  const handleCloseMassiveSMSDialog = () => {
    setSelectedFilters(null);
    setMassiveSMS(false);
    setDialogPage(0);
  };
  const handleChangeDialogPage = (number) => {
    const newPage = dialogPage + number;

    setDialogPage(newPage);
  };
  const handleSelectNewFilters = (filtersObject) => {
    setSelectedFilters(filtersObject);
  };

  const handleCloseMassiveDetails = () => {
    setMassiveDetails(null);
    setSearchValues(null); //next send opens without the previous search
  };
  const handleOpenMassiveDetails = (massiveId, campaignId) => {
    setMassiveDetails({ campaignId, massiveId });
  };

  const [expanded, setExpanded] = useState(false);

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getMassiveMessageDetails = useCallback(
    (pageNumber) => {
      return massiveDetails
        ? API.getMassiveDetails({
            pageNumber,
            filterExpression: mapFilters(
              ['fullNameSearch'],
              [{ keyName: 'parentId', operator: 'eq' }],
              [],
              {
                ...searchValues,
                parentId: massiveDetails?.massiveId,
              }
            ),
          })
        : Promise.resolve([]);
    },
    [massiveDetails, searchValues]
  );
  const {
    ObserverWrapper: MassiveDetailsObserverWrapper,
    removeClickedItem: removeCustomers,
    recordCount: customersCount,
    items: massiveRecipients,
  } = useInfiniteQuery(getMassiveMessageDetails);

  const getMassiveSMSHistory = useCallback(
    (pageNumber) =>
      API.getMassiveSMSHistory({
        pageNumber,
      }),
    []
  );
  const {
    ObserverWrapper,
    setNewItems,
    recordCount,
    items: campaignsList,
    updatePropertyValue: updateCustomersInvolved,
  } = useInfiniteQuery(getMassiveSMSHistory);

  useEffect(() => {
    API.getSectionTemplates(
      'MASSIVE',
      { silent: true },
      (result) => {
        setSMSTemplates(result);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_TEMPLATES_ERROR,
          severity: 'error',
        });
      }
    );
  }, []);

  useEffect(() => {
    API.getWaveSections(
      (res) => {
        setWaveSections(res);
      },
      () => {
        setWaveSections([]);
      }
    );
  }, []);

  useEffect(() => {
    if (selectedFilters) {
      const payload = { ...selectedFilters };
      if (payload?.scheduledOn) delete payload.scheduledOn;
      if (payload?.templateId) delete payload.templateId;

      API.getMassiveSMSPreview(
        payload,
        (response) => {
          setCustomersInvolvedCounts(response);
          handleChangeDialogPage(1);
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.MASSIVE_PREVIEW_ERROR,
            severity: 'error',
          });
        }
      );
    }
  }, [selectedFilters]);

  const handleSendMassiveSMS = (massiveObj) => {
    const { templateId, scheduledOn, campaignEndsOn, campaignName, ...others } =
      massiveObj;

    const payload = {
      campaign: {
        name: campaignName,
        templateId,
        scheduledOn: scheduledOn.toISOString(),
        endsOn: campaignEndsOn.toISOString(),
        customersInvolved: totalCustomersInvolved(customersInvolvedCounts), //somma dei valori dell'array di branches
      },
      filters: others,
    };

    API.sendMassiveSMS(
      payload,
      (result) => {
        if (result) setNewItems(result, recordCount + 1);
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_CREATED_SUCCESSFULLY,
          severity: 'success',
        });
        handleCloseMassiveSMSDialog();
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_CREATION_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleDeleteMassiveSMS = (activityId, campaignId) => {
    API.deleteMassiveSMS(
      activityId,
      { silent: true },
      () => {
        const smses = campaignsList.find(
          ({ id }) => id === campaignId
        )?.massiveSmses;
        const index = smses?.findIndex(
          ({ massiveId }) => massiveId === activityId
        );
        smses?.splice(index, 1);
        updateCustomersInvolved(campaignId, 'massiveSmses', smses);
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_DELETED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_DELETION_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleRemoveSMSToSelectedCustomers = (
    customersObj, //object contaning smsIds or EmailIds, isSMS property
    customersList, //selected customers to delete list
    onSuccess //onSuccess function callback
  ) => {
    const payload = { ...customersObj, activityId: massiveDetails.massiveId };
    API.removeMassiveToCustomers(
      payload, //array of activity ids if is SMS
      (newCustomersNumber) => {
        onSuccess();
        customersList.forEach((id) => removeCustomers(id, customersCount - 1));
        updateCustomersInvolved(
          massiveDetails.massiveId, //id
          MASSIVE_PROPERTIES.CUSTOMERS_NUMBER, //customersInvolved propertyName
          newCustomersNumber //number
        );
        setOpenSnackbarAdvise({
          message: intl.messages.CUSTOMERS_REMOVED,
          severity: 'info',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.REMOVING_CUSTOMERS_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const detailsCampaign = campaignsList.find(
    ({ id }) => id === massiveDetails?.campaignId
  );

  return (
    <>
      <SectionPage
        hideFilters
        hideSearchBar
        hideSorting
        hideExportExcel
        fab={intl.messages.NEW_MASSIVE}
        onClickFab={handleOpenMassiveSMSDialog}
        sectionName="MASSIVE_MESSAGES"
        customEntryFunction={(item) =>
          campaignEntry(
            item,
            handleDeleteMassiveSMS,
            SMSTemplates,
            handleOpenMassiveDetails,
            handleChangeAccordion,
            expanded
          )
        }
        ListObserverWrapper={ObserverWrapper}
        CustomEmptyList={EmptyMassiveMessagesComponent}
      />
      <FormProvider {...methods}>
        <TemplateMessageDialog
          id="massive-sms"
          open={massiveSMS}
          handleClose={handleCloseMassiveSMSDialog}
          title={
            dialogPage === 0
              ? intl.messages.FILTER_CUSTOMERS
              : intl.messages.SEND_MASSIVE
          }
          handleSubmit={
            dialogPage === 1 ? handleSendMassiveSMS : handleSelectNewFilters
          }
          actions={
            <CustomMassiveActions
              close={handleCloseMassiveSMSDialog}
              goBack={() => handleChangeDialogPage(-1)}
              currentPage={dialogPage}
            />
          }
          widgetWrapper={
            <MassiveSmsWrapper
              templates={groupTemplatesByName(SMSTemplates)}
              selectedFilters={selectedFilters}
              currentPage={dialogPage}
              customersInvolvedCounts={customersInvolvedCounts}
              waveSections={waveSections}
            />
          }
        />
      </FormProvider>
      <MassiveDetails
        campaign={detailsCampaign}
        massive={detailsCampaign?.massiveSmses?.find(
          ({ massiveId }) => massiveId === massiveDetails?.massiveId
        )}
        templateName={
          SMSTemplates.find(({ id }) => id === detailsCampaign?.templateId)
            ?.title
        }
        recipientsCount={customersCount}
        firstRecipient={massiveRecipients?.[0]}
        waveSections={waveSections}
        searchValues={searchValues}
        setSearchValues={setSearchValues}
        open={Boolean(massiveDetails)}
        close={handleCloseMassiveDetails}
        ObserverWrapper={MassiveDetailsObserverWrapper}
        removeCustomersFromBatch={handleRemoveSMSToSelectedCustomers}
      />
    </>
  );
};

export default MassiveSMSComponents;
